import { useCallback, useMemo } from 'react'
import { Container, Grid, Typography } from '@mui/material'
import parse from 'html-react-parser'

import Form from '@/components/base/form'
import Spacer from '@/components/sections/spacer'
import { useNotification } from '@/contexts/notification'
import { useAppStore } from '@/contexts/store'
import { isHappa } from '@/hooks/use-brand'
import { useLocaleStore } from '@/store/locale'
import { translateApiMessage } from '@/utils/api-messages'
import { NULL_SELECT_VALUE } from '@/utils/form/remote-select'

import { useAuth } from '@/api/auth'
import { uploadProfileIdDocument, uploadProfileLicense } from '@/api/auth/mutations'
import { useCountries } from '@/api/countries'
import { WPCF7FormField, WPPageData } from '@/types'

export type RegisterProps = {
  pageProps?: Omit<WPPageData, 'acf'> & {
    acf: {
      title?: string
      text?: string
      form?: {
        sign_label?: string
        cancel_label?: string
        form: {
          fields: WPCF7FormField[]
          button: {
            text: string
          }
        }
      }
    }
  }
}

// Fields that should be grouped inside owner_data
const OWNER_DATA_FIELDS = [
  'vat_number',
  'company_name',
  'paypal_email',
  'bank_account',
  'availability_hours',
  'law_431_98'
]

// Fields that should be grouped inside student_data
const STUDENT_DATA_FIELDS = [
  'student_type',
  'campus_id',
  'department',
  'managed_profile',
  'country_id',
  'university_id',
  'university_name',
  'course_id',
  'course_name',
  'reference_office',
  'contact_person',
  'contact_email',
  'contact_phone',
  'special_needs',
  'first_year'
]

const getSingleFileValue = (value: unknown): File | undefined => {
  if (!value) return undefined
  if (value instanceof File) return value
  if (Array.isArray(value)) {
    const first = value[0]
    return first instanceof File ? first : undefined
  }
  if (typeof FileList !== 'undefined' && value instanceof FileList) {
    return value.length > 0 ? value[0] : undefined
  }
  return undefined
}

// Helper to coerce managed_profile to boolean (may be "true"/"false" string)
const toBoolean = (value: unknown): boolean => {
  if (typeof value === 'string') return value === 'true'
  return Boolean(value)
}

const STUDENT_DATA_ID_FIELDS = ['country_id', 'university_id', 'course_id', 'campus_id'] as const

// Hidden fields configured in the CMS sometimes use the literal text "null" to mean "no value"
const normalizeNullLiteral = (value: unknown) =>
  typeof value === 'string' && value.trim().toLowerCase() === 'null' ? null : value

const normalizeIdFieldValue = (value: unknown) => {
  const resolved = normalizeNullLiteral(value)
  if (
    resolved === NULL_SELECT_VALUE ||
    resolved === '' ||
    resolved === null ||
    resolved === undefined
  ) {
    return null
  }

  const parsed = Number(resolved)
  return Number.isNaN(parsed) ? resolved : parsed
}

const Register = ({ pageProps }: RegisterProps) => {
  const acf = useMemo(() => pageProps?.acf, [pageProps])
  const form = useMemo(() => acf?.form?.form, [acf])
  const fields = useMemo(() => form?.fields || [], [form])
  const { strings } = useAppStore()
  const currentLocale = useLocaleStore((state) => state.currentLocale)

  const { register } = useAuth()
  const { collection: countries } = useCountries()
  const { notifySuccess, notifyError } = useNotification()

  const handleSubmit = useCallback(
    async (values: Record<string, any>) => {
      try {
        // Extract license and id_document files before building payload
        let licenseFile: File | undefined
        let idDocumentFile: File | undefined

        const normalizeCountryOfBirth = (value: unknown) => {
          const raw = value?.toString().trim() || ''
          if (!raw) return raw
          const countryId = Number(raw)
          if (Number.isNaN(countryId)) return raw
          return countries?.find((country) => country.id === countryId)?.name?.it || raw
        }

        // Map form values using adminLabel as keys, hidden fields use label
        const payload = fields.reduce(
          (acc, field) => {
            const inputKey = `input_${field.id}`
            const fileValue = getSingleFileValue(values[inputKey])

            // Extract files for separate upload
            if (
              (field.adminLabel === 'license' || field.label === 'license') &&
              fileValue instanceof File
            ) {
              licenseFile = fileValue
              return acc
            }
            if (
              (field.adminLabel === 'id_document' || field.label === 'id_document') &&
              fileValue instanceof File
            ) {
              idDocumentFile = fileValue
              return acc
            }

            if (field.type === 'hidden' && field.label && values[inputKey] !== undefined) {
              const defaultValue = normalizeNullLiteral(field.defaultValue)
              // Check if hidden field label belongs to owner_data or student_data
              if (OWNER_DATA_FIELDS.includes(field.label)) {
                acc.owner_data = acc.owner_data || {}
                // Force law_431_98 to boolean
                if (field.label === 'law_431_98') {
                  acc.owner_data[field.label] = toBoolean(defaultValue)
                } else {
                  acc.owner_data[field.label] = defaultValue
                }
              } else if (STUDENT_DATA_FIELDS.includes(field.label)) {
                acc.student_data = acc.student_data || {}
                // Force managed_profile to boolean
                if (field.label === 'managed_profile') {
                  acc.student_data[field.label] = toBoolean(defaultValue)
                } else if (
                  STUDENT_DATA_ID_FIELDS.includes(
                    field.label as (typeof STUDENT_DATA_ID_FIELDS)[number]
                  )
                ) {
                  acc.student_data[field.label] = normalizeIdFieldValue(defaultValue)
                } else {
                  acc.student_data[field.label] = defaultValue
                }
              } else if (field.label === 'country_of_birth') {
                acc[field.label] = normalizeCountryOfBirth(defaultValue)
              } else {
                acc[field.label] = defaultValue
              }
            } else if (field.adminLabel && values[inputKey] !== undefined) {
              // Check if field belongs to owner_data or student_data
              if (OWNER_DATA_FIELDS.includes(field.adminLabel)) {
                acc.owner_data = acc.owner_data || {}
                // Force law_431_98 to boolean
                if (field.adminLabel === 'law_431_98') {
                  acc.owner_data[field.adminLabel] = toBoolean(values[inputKey])
                } else {
                  acc.owner_data[field.adminLabel] = values[inputKey]
                }
              } else if (STUDENT_DATA_FIELDS.includes(field.adminLabel)) {
                acc.student_data = acc.student_data || {}
                // Force managed_profile to boolean
                if (field.adminLabel === 'managed_profile') {
                  acc.student_data[field.adminLabel] = toBoolean(values[inputKey])
                } else if (
                  STUDENT_DATA_ID_FIELDS.includes(
                    field.adminLabel as (typeof STUDENT_DATA_ID_FIELDS)[number]
                  )
                ) {
                  acc.student_data[field.adminLabel] = normalizeIdFieldValue(values[inputKey])
                } else {
                  acc.student_data[field.adminLabel] = values[inputKey]
                }
              } else if (field.adminLabel === 'country_of_birth') {
                acc[field.adminLabel] = normalizeCountryOfBirth(values[inputKey])
              } else {
                acc[field.adminLabel] = values[inputKey]
              }
            }
            return acc
          },
          {} as Record<string, any>
        )

        // Convert NULL_SELECT_VALUE sentinel to null in payload
        const normalizeNulls = (obj: Record<string, any>) => {
          for (const key of Object.keys(obj)) {
            if (obj[key] === NULL_SELECT_VALUE) {
              obj[key] = null
            } else if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
              normalizeNulls(obj[key])
            }
          }
        }
        normalizeNulls(payload)

        const response = await register({ ...payload, locale: currentLocale })

        // If registration successful and token received, upload documents
        if (response?.token && (licenseFile || idDocumentFile)) {
          const uploadPromises: Promise<{ message: string }>[] = []

          if (licenseFile) {
            uploadPromises.push(uploadProfileLicense(response.token, licenseFile))
          }
          if (idDocumentFile) {
            uploadPromises.push(uploadProfileIdDocument(response.token, idDocumentFile))
          }

          // Upload both documents simultaneously
          await Promise.all(uploadPromises)
        }

        if (response?.message) {
          notifySuccess(translateApiMessage(response.message, currentLocale))
        }
        return true
      } catch (error: any) {
        notifyError(error.message || strings?.register?.error?.registration_failed_fallback || '')
        return false
      }
    },
    [fields, register, notifyError, notifySuccess, currentLocale, strings, countries]
  )

  return (
    <>
      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 6 }} offset={{ sm: 1, md: 3 }}>
            {acf?.title && (
              <Typography
                variant="h4"
                fontWeight={isHappa ? 700 : 500}
                color={isHappa ? 'primary.main' : 'text.secondaryContrastText'}
              >
                {acf.title}
              </Typography>
            )}
            <Spacer size="xxs" />
            {acf?.text && (
              <Typography variant="textRegular" color="text.primary">
                {parse(acf.text)}
              </Typography>
            )}
            <Spacer size="xs" />
            <Form
              form_id={form?.fields[0].formId.toString() || ''}
              fields={form?.fields || []}
              submit={{
                text: form?.button.text || strings?.labels?.submit || '',
                color: 'primary'
              }}
              onSubmit={handleSubmit}
              customEndpoint={true}
              useGravity={false}
            />
          </Grid>
        </Grid>
      </Container>

      <Spacer size="2xl" />
    </>
  )
}

export default Register
